import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api, type LiveInterview, type SettingsStatus } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useHotkey } from '../lib/hooks';
import { useTheme } from '../lib/theme';
import { AppErrorBoundary, NewVersionBanner } from '../lib/version';
import { CommandPalette } from './CommandPalette';
import { Icon, type IconName } from './icons';
import { Avatar, Button, IconButton, Kbd, LogoMark, Menu, cx } from './ui';

export const SIDEBAR_W = 264;

function NavItem({ to, end, icon, children, badge, active }: { to: string; end?: boolean; icon: IconName; children: ReactNode; badge?: ReactNode; active?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => {
        const on = active ?? isActive;
        return cx(
          'group relative flex items-center gap-3 rounded-sm px-3 py-2 text-[13.5px] font-medium transition duration-150',
          on ? 'bg-surface text-fg shadow-card ring-1 ring-border dark:bg-white/8 dark:ring-white/10' : 'text-muted-fg hover:bg-surface/70 hover:text-fg dark:hover:bg-white/5',
        );
      }}
    >
      {({ isActive }) => {
        const on = active ?? isActive;
        return (
          <>
            {on && <span className="absolute -left-2 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-brand-gradient" aria-hidden />}
            <Icon name={icon} className={cx('h-[18px] w-[18px] transition', on ? 'text-accent' : 'text-faint group-hover:text-muted-fg')} />
            <span className="flex-1">{children}</span>
            {badge}
          </>
        );
      }}
    </NavLink>
  );
}

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="eyebrow mb-1.5 px-3 text-[10px]">{label}</p>
      <nav className="flex flex-col gap-0.5">{children}</nav>
    </div>
  );
}

const LLM_NAME: Record<string, string> = { anthropic: 'Claude', gemini: 'Gemini', mock: 'Mock' };

/** What works. The provider names behind it are shown to the platform's own admins only. */
function providerRows(s: SettingsStatus): Array<{ label: string; ok: boolean; value: string }> {
  const p = s.providers;
  const r = s.ready;
  if (!r) return [];
  const on = (ok: boolean, name?: string) => (ok ? (name ?? 'On') : 'Off');
  return [
    { label: 'AI', ok: r.ai, value: on(r.ai, p && (LLM_NAME[p.llm.provider] ?? p.llm.provider)) },
    { label: 'Voice', ok: r.voice, value: on(r.voice, p && 'Sarvam') },
    { label: 'Phone', ok: r.phone, value: on(r.phone, p?.telephony.provider) },
    { label: 'Email', ok: r.email, value: on(r.email, p && 'SMTP') },
  ];
}

function StatusCard() {
  const status = useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsStatus>('/api/settings/status'), staleTime: 60_000 });
  const rows = status.data?.ready ? providerRows(status.data) : [];
  const down = rows.filter((r) => !r.ok).length;
  const ok = status.data && down === 0;
  const headline = status.isError ? 'Status unavailable' : !status.data ? 'Checking…' : ok ? 'All systems live' : `${down} service${down > 1 ? 's' : ''} off`;
  return (
    <Link to="/admin/system" className="block rounded-md bg-night p-3.5 text-white ring-1 ring-white/10 transition hover:ring-white/25">
      <div className="flex items-center gap-2">
        <span className={cx('live-dot h-2 w-2 rounded-full', ok ? 'bg-[#4ade80] shadow-[0_0_10px_#4ade80]' : 'bg-[#fbbf24]')} aria-hidden />
        <span className="text-[13px] font-semibold">{headline}</span>
      </div>
      {rows.length > 0 && (
        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10.5px]">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-2">
              <dt className="text-white/45">{r.label}</dt>
              <dd className={r.ok ? 'text-[#4ade80]' : 'text-[#fbbf24]'}>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Link>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === 'dark';
  return <IconButton label={dark ? 'Switch to light theme' : 'Switch to dark theme'} icon={dark ? 'sun' : 'moon'} variant="secondary" onClick={toggle} />;
}

export function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const live = useQuery({ queryKey: ['live'], queryFn: () => api.get<LiveInterview[]>('/api/interviews/live'), refetchInterval: 10_000 });
  const liveCount = live.data?.filter((i) => i.status === 'IN_PROGRESS' || i.status === 'RINGING').length ?? 0;

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useHotkey(
    'k',
    useCallback(() => setPaletteOpen((o) => !o), []),
  );

  const onJobs = location.pathname.startsWith('/jobs') || location.pathname.startsWith('/candidates');
  const signOutNow = async () => {
    await signOut();
    qc.clear();
    navigate('/login');
  };

  const sidebar = (
    <div className="flex h-full flex-col gap-6 px-4 py-5">
      <Link to="/" className="flex items-center gap-3 px-1">
        <LogoMark className="h-9 w-9" />
        <span className="leading-tight">
          <span className="display block text-[19px] font-semibold text-fg">Eduvy</span>
          <span className="eyebrow block text-[10px]">Interviewer</span>
        </span>
      </Link>

      <Button variant="primary" icon="plus" to="/jobs/new" className="w-full">
        New job
      </Button>

      <div className="flex flex-col gap-5">
        <NavGroup label="Hiring">
          <NavItem to="/" end icon="home">
            Overview
          </NavItem>
          <NavItem to="/jobs" icon="briefcase" active={onJobs}>
            Jobs &amp; candidates
          </NavItem>
          <NavItem
            to="/live"
            icon="live"
            badge={
              liveCount > 0 ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-gradient px-1.5 font-mono text-[10px] font-semibold text-white shadow-brand" aria-label={`${liveCount} live`}>
                  {liveCount}
                </span>
              ) : undefined
            }
          >
            Live now
          </NavItem>
          <NavItem to="/scheduler" icon="calendar">
            Scheduler
          </NavItem>
        </NavGroup>
        <NavGroup label="Insight">
          <NavItem to="/analytics" icon="chart">
            Analytics
          </NavItem>
        </NavGroup>
        <NavGroup label="Workspace">
          <NavItem to="/settings" icon="settings">
            Settings
          </NavItem>
        </NavGroup>
        {user?.platformAdmin && (
          <NavGroup label="Platform">
            <NavItem to="/admin" icon="shield" active={location.pathname.startsWith('/admin')}>
              Console
            </NavItem>
          </NavGroup>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-3">
        {/* What runs the platform is the platform's own business (the owner, 2026-10-10): platform admins only. */}
        {user?.platformAdmin && <StatusCard />}
        {user && (
          <div className="flex items-center gap-2.5 rounded-md px-1 py-1">
            <Avatar name={user.name} size="sm" />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-[13px] font-semibold text-fg">{user.name}</span>
              <span className="block truncate text-[11px] text-muted-fg">{user.orgName}</span>
            </span>
            <Menu
              label="Account"
              size="sm"
              items={[
                { label: 'Settings', icon: 'settings', onSelect: () => navigate('/settings') },
                'divider',
                { label: 'Sign out', icon: 'logout', onSelect: () => void signOutNow(), hint: user.email },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-full">
      {/* Sidebar: fixed on large screens, a drawer below that. */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden border-r border-border bg-bg-deep lg:block" style={{ width: SIDEBAR_W }}>
        {sidebar}
      </aside>
      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[280px] border-r border-border bg-bg-deep shadow-pop">{sidebar}</aside>
        </div>
      )}

      <div className="lg:pl-[264px]">
        <header className="glass sticky top-0 z-30 border-b border-border">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <IconButton label="Open navigation" icon="menu" variant="secondary" className="lg:hidden" onClick={() => setMenuOpen(true)} />
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex h-10 w-full max-w-md items-center gap-3 rounded-sm bg-surface px-3.5 text-left text-sm text-faint ring-1 ring-inset ring-border transition hover:ring-border-strong dark:bg-white/4"
              aria-label="Jump to a job or a page"
            >
              <Icon name="search" className="h-4 w-4" />
              <span className="flex-1 truncate">Jump to a job or a page…</span>
              <span className="hidden items-center gap-1 sm:flex">
                <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </span>
            </button>
            <div className="ml-auto flex items-center gap-2">
              <ThemeToggle />
              {user && (
                <span className="hidden items-center gap-2 rounded-full bg-surface py-1 pl-1 pr-3 text-[13px] font-medium text-fg-2 ring-1 ring-inset ring-border md:inline-flex dark:bg-white/4" title={`${user.name} · ${user.email}`}>
                  <Avatar name={user.name} size="xs" />
                  <span className="max-w-[180px] truncate">{user.orgName}</span>
                </span>
              )}
              <IconButton label="Sign out" icon="logout" variant="secondary" className="md:hidden" onClick={() => void signOutNow()} />
            </div>
          </div>
        </header>
        <NewVersionBanner />
        <main className="mx-auto max-w-[1320px] px-4 py-8 sm:px-6 lg:px-8">
          {/* Keyed by the page: moving to another page clears a page's error and replays the entrance. */}
          <AppErrorBoundary key={location.pathname}>
            <div className="fade-up">
              <Outlet />
            </div>
          </AppErrorBoundary>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
